import { LeadsTableTab, OrdersTableTab, PREORDERS_TABLE_TAB } from '@ekhodealer/ekho-common';

import type { PreordersTableTab } from '@ekhodealer/ekho-common';
import type { SettingsSection } from './settingsSections';

import { DiscountTab } from './discountTabs';

export const paths: Record<
	string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- route params vary per path, generic args unavoidable
	{ path: string; relativePath?: string; getHref: (...args: any[]) => string }
> = {
	home: {
		path: '/',
		getHref: () => '/',
	},

	login: {
		path: '/login',
		getHref: () => '/login',
	},

	setup: {
		path: '/setup/*',
		getHref: (rest?: string) => (rest ? `/setup/${rest}` : '/setup'),
	},
	setupProfile: {
		path: '/setup/profile',
		relativePath: 'profile',
		getHref: () => '/setup/profile',
	},
	setupVerifyEmail: {
		path: '/setup/verify-email',
		relativePath: 'verify-email',
		getHref: () => '/setup/verify-email',
	},
	setupMfa: {
		path: '/setup/mfa',
		relativePath: 'mfa',
		getHref: () => '/setup/mfa',
	},

	forgotPassword: {
		path: '/forgot-password',
		getHref: () => '/forgot-password',
	},

	resetPassword: {
		path: '/reset-password',
		getHref: () => '/reset-password',
	},

	noAccess: {
		path: '/no-access',
		getHref: () => '/no-access',
	},

	userManagement: {
		path: '/auth/usermgmt',
		getHref: () => '/auth/usermgmt',
	},

	dealerOnboarding: {
		path: '/:servicesProviderId/onboarding',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/onboarding`,
	},

	servicesProviderConfiguration: {
		path: '/:servicesProviderId/configuration',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/configuration`,
	},

	servicesProviderConfigurationTab: {
		path: '/:servicesProviderId/configuration/:tabId',
		getHref: (servicesProviderId: string, tabId: string, section?: string) => {
			const basePath = `/${servicesProviderId}/configuration/${tabId}`;
			return section ? `${basePath}?section=${encodeURIComponent(section)}` : basePath;
		},
	},

	settingsTeam: {
		path: '/:servicesProviderId/settings/account/team',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/settings/account/team`,
	},

	inventory: {
		path: '/:servicesProviderId/inventory',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/inventory`,
	},

	inventoryDetails: {
		path: '/:servicesProviderId/inventory/:inventoryItemId/:tab',
		getHref: (servicesProviderId: string, inventoryItemId: string, tab: string) =>
			`/${servicesProviderId}/inventory/${inventoryItemId}/${tab}`,
	},

	plan_details: {
		path: '/:servicesProviderId/plan-details',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/plan-details`,
	},

	usage: {
		path: '/:servicesProviderId/usage',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/usage`,
	},

	analytics: {
		path: '/:servicesProviderId/analytics',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/analytics`,
	},

	leads: {
		path: '/:servicesProviderId/leads',
		getHref: (servicesProviderId: string, tabId?: LeadsTableTab) =>
			`/${servicesProviderId}/leads/${tabId || LeadsTableTab.ALL}`,
	},

	leadsTab: {
		path: '/:servicesProviderId/leads/:tabId',
		getHref: (servicesProviderId: string, tabId: LeadsTableTab, viewType?: 'table' | 'board') =>
			`/${servicesProviderId}/leads/${tabId}${viewType ? `/${viewType}` : ''}`,
	},

	leadDetails: {
		path: '/:servicesProviderId/leads/:tabId/:leadId/:detailsTab',
		getHref: (
			servicesProviderId: string,
			tabId: LeadsTableTab,
			leadId: string,
			detailsTab: string,
		) => `/${servicesProviderId}/leads/${tabId}/${leadId}/${detailsTab}`,
	},

	orders: {
		path: '/:servicesProviderId/orders',
		getHref: (servicesProviderId: string, tabId?: OrdersTableTab) =>
			`/${servicesProviderId}/orders/${tabId || OrdersTableTab.ALL}`,
	},

	ordersTab: {
		path: '/:servicesProviderId/orders/:tabId',
		getHref: (servicesProviderId: string, tabId: OrdersTableTab) =>
			`/${servicesProviderId}/orders/${tabId}`,
	},

	preorders: {
		path: '/:servicesProviderId/preorders',
		getHref: (servicesProviderId: string, tabId?: PreordersTableTab) =>
			`/${servicesProviderId}/preorders/${tabId || PREORDERS_TABLE_TAB.ALL}`,
	},

	preordersTab: {
		path: '/:servicesProviderId/preorders/:tabId',
		getHref: (servicesProviderId: string, tabId: PreordersTableTab) =>
			`/${servicesProviderId}/preorders/${tabId}`,
	},

	preorderDetails: {
		path: '/:servicesProviderId/preorders/:tabId/:preorderId/:detailsTab',
		getHref: (
			servicesProviderId: string,
			tabId: PreordersTableTab,
			preorderId: string,
			detailsTab: string,
		) => `/${servicesProviderId}/preorders/${tabId}/${preorderId}/${detailsTab}`,
	},

	orderDetails: {
		path: '/:servicesProviderId/orders/:tabId/:orderId/:detailsTab',
		getHref: (
			servicesProviderId: string,
			tabId: OrdersTableTab,
			orderId: string,
			detailsTab: string,
		) => `/${servicesProviderId}/orders/${tabId}/${orderId}/${detailsTab}`,
	},

	discounts: {
		path: '/:servicesProviderId/discounts',
		getHref: (servicesProviderId: string, tabId?: DiscountTab) =>
			`/${servicesProviderId}/discounts/${tabId || DiscountTab.ALL}`,
	},

	discountsTab: {
		path: '/:servicesProviderId/discounts/:tabId',
		getHref: (servicesProviderId: string, tabId: DiscountTab) =>
			`/${servicesProviderId}/discounts/${tabId}`,
	},

	discountDetails: {
		path: '/:servicesProviderId/discounts/:tabId/:discountId/:detailsTab',
		getHref: (
			servicesProviderId: string,
			tabId: DiscountTab,
			discountId: string,
			detailsTab: string,
		) =>
			`/${servicesProviderId}/discounts/${tabId}/${encodeURIComponent(discountId)}/${detailsTab}`,
	},

	conversations: {
		path: '/:servicesProviderId/conversations',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/conversations`,
	},

	conversation: {
		path: '/:servicesProviderId/conversations/:conversationId',
		getHref: (servicesProviderId: string, conversationId: string) =>
			`/${servicesProviderId}/conversations/${conversationId}`,
	},

	reports: {
		path: '/:servicesProviderId/reports',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/reports`,
	},

	reportsOrders: {
		path: '/:servicesProviderId/reports/orders',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/reports/orders`,
	},

	reportsPayments: {
		path: '/:servicesProviderId/reports/payments',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/reports/payments`,
	},

	settings: {
		path: '/:servicesProviderId/settings',
		getHref: (servicesProviderId: string) => `/${servicesProviderId}/settings`,
	},

	settingsSection: {
		path: '/:servicesProviderId/settings/*',
		getHref: (servicesProviderId: string, section: SettingsSection) =>
			`/${servicesProviderId}/settings/${section}`,
	},

	// Product onboarding (self-serve, no guard — new dealers don't have portal access yet)
	onboarding: {
		path: '/onboarding',
		getHref: () => '/onboarding',
	},
	onboardingProducts: {
		path: '/onboarding/products',
		relativePath: 'products',
		getHref: () => '/onboarding/products',
	},
	onboardingSubscribe: {
		path: '/onboarding/subscribe',
		relativePath: 'subscribe',
		getHref: () => '/onboarding/subscribe',
	},
	onboardingProcessing: {
		path: '/onboarding/processing',
		relativePath: 'processing',
		getHref: () => '/onboarding/processing',
	},

	// Existing dealer product subscribe flow — svpId in URL encodes dealer identity
	svpSubscribe: {
		path: '/:svpId/subscribe',
		getHref: (svpId: string) => `/${svpId}/subscribe`,
	},
	svpSubscribeProducts: {
		path: '/:svpId/subscribe/products',
		relativePath: 'products',
		getHref: (svpId: string) => `/${svpId}/subscribe/products`,
	},
	svpSubscribeProcessing: {
		path: '/:svpId/subscribe/processing',
		relativePath: 'processing',
		getHref: (svpId: string) => `/${svpId}/subscribe/processing`,
	},

	signout: {
		path: '/signout',
		getHref: () => '/signout',
	},

	mintlifyLogin: {
		path: '/auth/mintlify',
		getHref: (redirect?: string) => {
			const base = '/auth/mintlify';
			return redirect ? `${base}?redirect=${encodeURIComponent(redirect)}` : base;
		},
	},

	// Debug routes (dev-only)
	debugAuth: {
		path: '/debug/auth',
		getHref: () => '/debug/auth',
	},
} as const;

export type Paths = typeof paths;
